import type { MarketedMembershipPlanType } from '@frontend/api-client';
import type { Card, CreditPackage } from '@frontend/api-types';
import type { SubscriptionPlan } from '@frontend/react-domain/subscriptions';
import type { Reducer } from 'react';

export type BuyCreditStep =
  | 'changeSubscription'
  | 'failedPayment'
  | 'processing'
  | 'selectCard'
  | 'selectPackage'
  | 'success';

export type BuyCreditsState = {
  canGoBack?: boolean;
  card?: Card;
  package?: CreditPackage;
  step: BuyCreditStep;
  subscriptionPlan?: SubscriptionPlan;
};

export type PurchasePackage =
  | { package: CreditPackage; type: 'credits' }
  | { planType: MarketedMembershipPlanType; type: 'subscription' };

export type BuyCreditsStateMachineAction =
  | { card: Card; package?: CreditPackage; subscriptionPlan?: SubscriptionPlan; type: 'completePayment' }
  | { card: Card; package?: CreditPackage; subscriptionPlan?: SubscriptionPlan; type: 'failPayment' }
  | { card: Card; package?: CreditPackage; subscriptionPlan?: SubscriptionPlan; type: 'pay' }
  | { package: CreditPackage; type: 'selectPackage' }
  | { subscriptionPlan: SubscriptionPlan; type: 'continueWithPlanSelection' }
  | { type: 'back' }
  | { type: 'reset' }
  | { type: 'startSubscriptionChange' };

export const DEFAULT_STEP_TITLE_LOOKUP: Partial<Record<BuyCreditStep, (state: BuyCreditsState) => string>> = {
  changeSubscription: () => 'Subscribe and Save',
  selectCard: (state) => (state.subscriptionPlan ? 'Update membership' : 'Purchase credits'),
  selectPackage: () => 'Credits',
};

const canGoBack = (step: BuyCreditStep, goBackSteps: BuyCreditStep[] = ['selectCard', 'changeSubscription']) => {
  return goBackSteps.includes(step);
};

const initialState: BuyCreditsState = {
  canGoBack: canGoBack('selectPackage'),
  step: 'selectPackage',
};

export const buyCreditsStateMachine: Reducer<BuyCreditsState, BuyCreditsStateMachineAction> = (prev, action) => {
  if (action.type === 'reset') {
    return { ...initialState };
  }

  switch (prev.step) {
    case 'selectPackage':
      if (action.type === 'selectPackage') {
        return {
          canGoBack: canGoBack('selectCard'),
          package: action.package,
          step: 'selectCard',
        };
      } else if (action.type === 'startSubscriptionChange') {
        return {
          canGoBack: canGoBack('changeSubscription'),
          step: 'changeSubscription',
        };
      }
      break;

    case 'changeSubscription':
      if (action.type === 'continueWithPlanSelection') {
        return {
          canGoBack: canGoBack('selectCard'),
          step: 'selectCard',
          subscriptionPlan: action.subscriptionPlan,
        };
      } else if (action.type === 'back') {
        if (!canGoBack(prev.step)) {
          return prev;
        }
        return {
          canGoBack: canGoBack('selectPackage'),
          step: 'selectPackage',
        };
      }
      break;

    case 'selectCard':
      if (action.type === 'pay') {
        return {
          canGoBack: false,
          card: action.card,
          package: action.package,
          step: 'processing',
          subscriptionPlan: action.subscriptionPlan,
        };
      } else if (action.type === 'back') {
        if (!canGoBack(prev.step)) {
          return prev;
        }

        if (prev.subscriptionPlan) {
          return {
            canGoBack: canGoBack('changeSubscription'),
            step: 'changeSubscription',
            subscriptionPlan: prev.subscriptionPlan,
          };
        }

        return {
          canGoBack: canGoBack('selectPackage'),
          package: prev.package,
          step: 'selectPackage',
        };
      }
      break;

    case 'processing':
      if (action.type === 'completePayment') {
        return {
          canGoBack: false,
          card: action.card,
          package: action.package,
          step: 'success',
          subscriptionPlan: action.subscriptionPlan,
        };
      } else if (action.type === 'failPayment') {
        return {
          canGoBack: false,
          card: action.card,
          package: action.package,
          step: 'failedPayment',
          subscriptionPlan: action.subscriptionPlan,
        };
      }
      break;

    case 'success':
    case 'failedPayment':
      break;

    default:
      break;
  }

  return prev;
};

export { initialState as buyCreditsInitialState };
